import type { PaletteClassName } from '../../data/types';
import type { WaterClass } from '../../data/waterClasses';
import type { Vec2 } from '../../geo/types';
import type { SiteQueries, WaterKind } from '../../site/types';
import type { VertexLabeller } from './densify';
import { clearestCornerProbes, type CornerProbe } from './corners';
import { areaClassAt, type WaterAreaClass } from './waterAreas';
import { bodyOfWater } from './waterBodies';

// Area water has no class of its own: it takes the class of the area that holds it.
export const WATER_KIND_TO_PALETTE: Readonly<Record<Exclude<WaterKind, 'area'>, WaterClass>> = {
  ij: 'ijWater',
  harbour: 'ijWater',
  river: 'riverWater',
  canal: 'canalWater',
  moat: 'canalWater',
  ditch: 'canalWater',
};

// The site queries treat water outside every channel and area as IJ, and the far ring continues the IJ.
export const FALLBACK_WATER_CLASS: PaletteClassName = WATER_KIND_TO_PALETTE.ij;

// The site's kind of water at a vertex, and the class its colour takes from it.
export interface WaterLabel {
  readonly kind: WaterKind;
  readonly cls: PaletteClassName;
}

export type WaterSample = readonly [number, number, WaterLabel | null];

export const FALLBACK_WATER_LABEL: WaterLabel = { kind: 'ij', cls: FALLBACK_WATER_CLASS };

// Triangles across a class boundary are split down to this edge length, so the colour ramp between two
// classes is at most about two pixels wide in the painter view (0.75 m per pixel on the ground at the city).
export const KIND_BOUNDARY_EDGE_M = 2;

// Well past the 1 cm the site outlines stray from the channel strips they were merged from, and short
// of the narrowest ditch, so the probe classifies the water the vertex sits in.
export const KIND_PROBE_M = 0.25;

// A probe nearer than this to an edge of its triangle may still sit on an outline, where the kind
// query is ambiguous; its vertex takes a neighbour's class instead.
export const MIN_PROBE_CLEARANCE_M = 0.02;

// The refinement labels vertices by class alone, so it splits only where the colour ramps. Kinds of one
// class blend across whole triangles where they meet: the IJ and its harbour, canals, moats and ditches.
// Splitting the one such edge the IJ has, at the harbour mouth, adds 2% more triangles and 5% more time to
// a stage at its budget.
export interface WaterClassSampler extends VertexLabeller {
  labelAt(vertex: Vec2, probe: CornerProbe): PaletteClassName | null;
  // The kind and class a probe finds for a vertex, kept for it like its label.
  waterAt(vertex: Vec2, probe: CornerProbe): WaterLabel | null;
  // The kind and class kept for a vertex, or undefined while it has none.
  recall(vertex: Vec2): WaterLabel | null | undefined;
  retained(): readonly WaterSample[];
}

export function waterClassAt(
  kind: WaterKind,
  p: Vec2,
  areas: readonly WaterAreaClass[]
): PaletteClassName {
  return kind === 'area'
    ? (areaClassAt(areas, p) ?? FALLBACK_WATER_CLASS)
    : WATER_KIND_TO_PALETTE[kind];
}

// The class each body of water takes, by its layout id; a body without one takes the class of its
// kind.
export type WaterBodyClasses = Readonly<Record<string, WaterClass | undefined>>;

// Channels come first in the kind order, and the channel query skips the water containment test that
// makes up most of a kind query; probes lie inside the water mesh, so nothing else changes.
function labelOfWater(
  queries: SiteQueries,
  areas: readonly WaterAreaClass[],
  bodies: WaterBodyClasses,
  p: Vec2
): WaterLabel | null {
  const channel = queries.channelAt(p);
  const kind = channel?.kind ?? queries.waterKindAt(p);
  if (kind === null) return null;
  return {
    kind,
    cls: bodies[bodyOfWater(channel, kind, areas, p)] ?? waterClassAt(kind, p, areas),
  };
}

// The first answer for a vertex is kept, keyed by its float32 position: the refinement splits on it with
// layout doubles, and the colouring must read the same class back from the built Float32 mesh.
export function createWaterClassSampler(
  queries: SiteQueries,
  areas: readonly WaterAreaClass[],
  bodies: WaterBodyClasses = {},
  retained: readonly WaterSample[] = []
): WaterClassSampler {
  const known = new Map<number, Map<number, WaterLabel | null>>();
  for (const [x, y, label] of retained) {
    let column = known.get(x);
    if (!column) known.set(x, (column = new Map()));
    column.set(y, label);
  }
  const recall = (vertex: Vec2) => known.get(Math.fround(vertex[0]))?.get(Math.fround(vertex[1]));
  const labelOf = ({ point, clearance }: CornerProbe): WaterLabel | null =>
    clearance < MIN_PROBE_CLEARANCE_M ? null : labelOfWater(queries, areas, bodies, point);
  const waterAt = (vertex: Vec2, probe: CornerProbe): WaterLabel | null => {
    const kept = recall(vertex);
    if (kept !== undefined) return kept;
    const label = labelOf(probe);
    const x = Math.fround(vertex[0]);
    known.set(
      x,
      (known.get(x) ?? new Map<number, WaterLabel | null>()).set(Math.fround(vertex[1]), label)
    );
    return label;
  };
  return {
    reach: KIND_PROBE_M,
    recall,
    retained: () =>
      [...known].flatMap(([x, column]) =>
        [...column].map(([y, label]): WaterSample => [x, y, label])
      ),
    waterAt,
    labelAt: (vertex, probe) => waterAt(vertex, probe)?.cls ?? null,
  };
}

function neighboursOf(vertexCount: number, indices: ArrayLike<number>): readonly number[][] {
  const neighbours = Array.from({ length: vertexCount }, (): number[] => []);
  for (let t = 0; t < indices.length; t += 3) {
    const corners = [indices[t]!, indices[t + 1]!, indices[t + 2]!];
    corners.forEach((v, i) => neighbours[v]!.push(corners[(i + 1) % 3]!, corners[(i + 2) % 3]!));
  }
  return neighbours;
}

function nearestOf(p: Vec2, candidates: readonly number[], pointAt: (v: number) => Vec2): number {
  const distance = (v: number) => Math.hypot(pointAt(v)[0] - p[0], pointAt(v)[1] - p[1]);
  return candidates.reduce((best, v) => (distance(v) < distance(best) ? v : best));
}

// A vertex the probe cannot place takes the label of its nearest placed neighbour. A sliver along a bank
// can hold a long run of such vertices; it fills in from its ends, one ring of neighbours at a time.
export function borrowedLabels<T>(
  own: readonly (T | null)[],
  indices: ArrayLike<number>,
  pointAt: (v: number) => Vec2
): ReadonlyMap<number, T> {
  const neighbours = neighboursOf(own.length, indices);
  const borrowed = new Map<number, T>();
  const labelOf = (v: number) => own[v] ?? borrowed.get(v) ?? null;
  let pending = own.flatMap((label, v) => (label === null ? [v] : []));
  for (;;) {
    const ready = pending.flatMap((u) => {
      const placed = neighbours[u]!.filter((w) => labelOf(w) !== null);
      return placed.length > 0
        ? [[u, labelOf(nearestOf(pointAt(u), placed, pointAt))!] as const]
        : [];
    });
    if (ready.length === 0) return borrowed;
    for (const [u, label] of ready) borrowed.set(u, label);
    pending = pending.filter((u) => !borrowed.has(u));
  }
}

// Positions are three.js world coordinates (x, y, z = -north).
export function vertexWaterLabels(
  positions: ArrayLike<number>,
  indices: ArrayLike<number>,
  sampler: WaterClassSampler
): readonly WaterLabel[] {
  const pointAt = (v: number): Vec2 => [positions[v * 3]!, -positions[v * 3 + 2]!];
  const kept = Array.from({ length: positions.length / 3 }, (_, v) => sampler.recall(pointAt(v)));
  const probes = clearestCornerProbes(
    pointAt,
    indices,
    (v) => kept[v] === undefined,
    sampler.reach
  );
  const own = kept.map((label, v) => {
    if (label !== undefined) return label;
    const probe = probes.get(v);
    return probe ? sampler.waterAt(pointAt(v), probe) : null;
  });
  const borrowed = borrowedLabels(own, indices, pointAt);
  return own.map((label, v) => label ?? borrowed.get(v) ?? FALLBACK_WATER_LABEL);
}
